import { useAppDispatch, useAppSelector } from "@/app/appStore";
import {
  captureAnnotationData,
  selectAnnotationData,
  selectAnnotationView,
  selectCurrentCamera,
  selectGondolas,
  selectIsEditable,
  selectIsSkuSectionPlanogramVisible,
  selectSelectedGondola,
  selectSelectedJourneyCameraEvents,
  selectSelectedJourneyEvents,
  setCurrentCamera,
  setGondolaDropdownValue,
  setSelectedGondola,
  updateCanvasDimensions,
} from "@/entities/annotation";
import { Player, usePlayer, usePlayerStore } from "@tezzro/player";
import { Flex, Text } from "@radix-ui/themes";
import { useMemo, useCallback } from "react";
import { Circle, Group, Path } from "react-konva";
import Konva from "konva";
import { Canvas2DWrapper } from "@/features/annotation/ui/canvas-2d-wrapper";
import { Stage } from "konva/lib/Stage";
import { ZoneProjections } from "@/features/zone-projections";
import { Order } from "@/shared/api/lod-api";
import { ZoneProjectionPolygons } from "@/features/annotation/ui/zone-projection-polygons";
import MdiVideoOffOutline from "~icons/mdi/video-off-outline";
import { ANNOTATION_TYPES } from "@/entities/annotation/model/consts";
import toast from "react-hot-toast";
import { GondolaProjections } from "./gondola-projections";
import { usePlanogram } from "../lib/usePlanogram";
import { useCalibration } from "../lib/useCalibration";
import { useCameras } from "../hooks/useCameras";
import { useSchedules } from "../lib/useSchedules";
import { Dimensions } from "@/shared";
import SkuSectionPlanogram from "@/features/sku-sections/components/sku-section-planogram";
import { ZoomPanWrapper } from "@/shared/components/zoom-pan-wrapper";

export function AnnotationWorkspace({
  selectedOrder,
  hasZoneProjections = true,
  isClickable = true,
  renderToCanvas,
  renderOverlay,
}: {
  selectedOrder: Order | null;
  hasZoneProjections?: boolean;
  isClickable?: boolean;
  renderToCanvas?: () => React.ReactNode;
  renderOverlay?: () => React.ReactNode;
}) {
  const { planogram } = usePlanogram();
  const { calibration } = useCalibration();
  const cameraData = useCameras();

  const dispatch = useAppDispatch();
  const { getState } = usePlayerStore();
  const player = usePlayer();
  const annotationView = useAppSelector(selectAnnotationView);
  const isEditable = useAppSelector(selectIsEditable);
  const selectedGondola = useAppSelector(selectSelectedGondola);
  const selectedCamera = useAppSelector(selectCurrentCamera);
  const isSkuSectionPlanogramVisible = useAppSelector(
    selectIsSkuSectionPlanogramVisible
  );
  const gondolas = useAppSelector(selectGondolas);
  const selectedJourneyId = useAppSelector(
    (state) => state.annotation.selectedJourneyId
  );
  const selectedJourneyEvents = useAppSelector(selectSelectedJourneyEvents);
  const canvasDimensions = useAppSelector(
    (state) => state.annotation.canvasDimensions
  );
  const annotationData = useAppSelector(selectAnnotationData);
  const pinCoordinates = annotationData?.pinCoordinates;
  const isPinVisible = annotationData?.cameraId === selectedCamera;

  const isObserverCamera = useMemo(() => {
    return !!planogram?.observing_cameras_masks?.find(
      (item) => item.camera_local_id === selectedCamera
    );
  }, [planogram, selectedCamera]);

  const onCanvasResize = useCallback(
    (newSize: Dimensions) => dispatch(updateCanvasDimensions(newSize)),
    [dispatch]
  );

  const onPointerDown = (event: Konva.KonvaEventObject<PointerEvent>) => {
    if (event.evt.ctrlKey) {
      return;
    }

    const handTrackingCameras = selectedGondola?.handTrackingCameras;
    const hasGondolaData = handTrackingCameras?.some(
      (camera) => camera.id === selectedCamera
    );
    const videoElement = player.mediaPlayer.getVideoElement();

    if (isObserverCamera) {
      const gondolaName = event.target.attrs.id;
      const gondola = gondolas.find((gondola) => gondola.name === gondolaName);

      const isClickedOnGondolaPlanogram = gondolaName && gondola;

      if (!isClickedOnGondolaPlanogram) return;

      if (gondola) {
        dispatch(setSelectedGondola(gondola));
        dispatch(setGondolaDropdownValue(gondola));
        const handTrackingCameraId = gondola.handTrackingCameras?.[0].id;

        dispatch(setCurrentCamera(handTrackingCameraId));
        cameraData.setCameraModeFromCameraId(handTrackingCameraId);
        player.changeCamera(handTrackingCameraId);
      }
      return;
    }

    if (!isEditable) {
      return toast.error("This view is read-only. Markers cannot be added");
    }

    if (!selectedJourneyId) {
      return toast.error("Please select a journey to continue");
    }

    if (annotationView === ANNOTATION_TYPES.PROXIMITY_TIME_RANGES) {
      return toast.error("Markers cannot be added in this mode");
    }

    if (
      !videoElement ||
      !videoElement.videoWidth ||
      !videoElement.videoHeight
    ) {
      toast.error(
        "Video element not found, please check if the video is loaded and try again"
      );
      return;
    }

    const pointerAbsolutePosition = (
      event.currentTarget as Stage
    ).getPointerPosition();
    const hasAllEventsCreated = selectedJourneyEvents.length === 2;

    if (!pointerAbsolutePosition) {
      return toast.error("Unable to determine marker position");
    }

    if (!planogram) {
      return toast.error("Planogram data not found");
    }

    if (!calibration) {
      return toast.error("Calibration data not found");
    }

    if (hasAllEventsCreated && annotationView === ANNOTATION_TYPES.JOURNEY) {
      return toast.error(
        "Maximum number of journey events (2) has been reached"
      );
    }

    if (isSkuSectionPlanogramVisible) {
      return toast.error("Please exit SKU Planogram View");
    }

    if (
      !hasGondolaData &&
      (annotationView === ANNOTATION_TYPES.TRANSACTION ||
        annotationView === ANNOTATION_TYPES.PROXIMITY)
    ) {
      return toast.error(
        "Please select a gondola and switch to its hand tracking camera"
      );
    }

    if (
      !planogram ||
      !pointerAbsolutePosition ||
      (hasAllEventsCreated && annotationView === ANNOTATION_TYPES.JOURNEY) ||
      !isEditable
    ) {
      return;
    }

    player.mediaPlayer.pause();

    const pointerRelativePosition = {
      relativeX: pointerAbsolutePosition.x / canvasDimensions.width,
      relativeY: pointerAbsolutePosition.y / canvasDimensions.height,
    };

    dispatch(
      captureAnnotationData({
        pinCoordinates: pointerRelativePosition,
        timestamp: new Date(getState().currentTime).toISOString(),
        calibrationId: calibration.id,
        planogramId: planogram.id,
        cameraImageWidth: videoElement.videoWidth,
        cameraImageHeight: videoElement.videoHeight,
      })
    );
  };

  const renderZoneProjections = () => {
    if (!hasZoneProjections) return null;

    if (isObserverCamera) {
      return (
        <GondolaProjections
          selectedCamera={selectedCamera}
          canvasDimensions={canvasDimensions}
          imageSize={{ width: 1920, height: 1080 }}
          observingCamerasMasks={planogram?.observing_cameras_masks}
        />
      );
    }

    if (!isCameraCalibrated) return null;

    return (
      <ZoneProjections
        activeSchedule={activeSchedule}
        selectedCamera={selectedCamera}
        imageDimensions={canvasDimensions}
        imageSize={{ width: 1920, height: 1080 }}
        isOverflownProjectionsVisible={false}
        getOnDateTime={() => selectedOrder?.time || ""}
      >
        {(props) => (
          <ZoneProjectionPolygons
            canvasDimensions={canvasDimensions}
            highlightedZoneId={selectedOrder?.zone?.zone_id}
            {...props}
          />
        )}
      </ZoneProjections>
    );
  };

  const { activeSchedule } = useSchedules({ queryKey: "zoneHistorySchedules" });

  const isCameraCalibrated = useMemo(() => {
    const isCameraAvailable = cameraData.allCameras.some(
      (camera) => camera.id === selectedCamera
    );

    const isCalibrated = calibration?.camera_calibrations.some(
      (calibration) => calibration.camera_id === selectedCamera
    );

    return isCameraAvailable && isCalibrated;
  }, [selectedCamera, cameraData, calibration]);

  if (!selectedOrder) {
    return <NoOrderSelected />;
  }

  return (
    <ZoomPanWrapper key={selectedCamera}>
      <Flex justify="center" className="absolute z-10 w-full h-full">
        <Canvas2DWrapper
          size={canvasDimensions}
          onResize={onCanvasResize}
          renderVideo={<Player.Video hotkeysEnabled={false} />}
          onPointerDown={
            isClickable && !isSkuSectionPlanogramVisible
              ? onPointerDown
              : undefined
          }
        >
          {renderToCanvas?.()}
          {renderZoneProjections()}
          {isSkuSectionPlanogramVisible && <SkuSectionPlanogram />}
          <EventMarks
            canvasDimensions={canvasDimensions}
            selectedCamera={selectedCamera}
          />

          {pinCoordinates && isPinVisible && (
            <Crosshair
              x={canvasDimensions.width * pinCoordinates.relativeX}
              y={canvasDimensions.height * pinCoordinates.relativeY}
              isActive={false}
            />
          )}
        </Canvas2DWrapper>
        {renderOverlay?.()}
      </Flex>
    </ZoomPanWrapper>
  );
}

function NoOrderSelected() {
  return (
    <Flex justify="center" className="absolute z-10 w-full h-full">
      <Flex
        justify="center"
        className="text-white"
        align="center"
        direction="column"
      >
        <MdiVideoOffOutline
          width={100}
          height={100}
          className="text-neutral-500"
        />
        <Text highContrast>Please, select an order from right sidebar</Text>
      </Flex>
    </Flex>
  );
}

function EventMarks({
  selectedCamera,
  canvasDimensions,
}: {
  selectedCamera: string;
  canvasDimensions: Dimensions;
}) {
  const events = useAppSelector((state) =>
    selectSelectedJourneyCameraEvents(state, selectedCamera)
  );
  const selectedEvent = useAppSelector(
    (state) => state.annotation.selectedEvent
  );

  return events.map(
    (event) =>
      event?.pinCoordinates && (
        <Crosshair
          key={event.id}
          isActive={selectedEvent?.id === event.id}
          x={event?.pinCoordinates?.relativeX * canvasDimensions.width}
          y={event?.pinCoordinates?.relativeY * canvasDimensions.height}
        />
      )
  );
}

function Crosshair({
  x,
  y,
  isActive,
}: Omit<Konva.ImageConfig, "image"> & { isActive: boolean }) {
  return (
    <Group listening={false}>
      <Circle
        x={x}
        y={y}
        radius={11}
        fill={isActive ? "black" : "rgba(0, 7, 27, 0.5)"}
        strokeWidth={1}
        stroke={isActive ? "black" : "rgba(0, 7, 27, 0.5)"}
      />
      <Path
        x={x - 12}
        y={y - 12}
        fill={isActive ? "white" : "rgba(255, 255, 255, 0.7)"}
        data="M17 13h-4v4h-2v-4H7v-2h4V7h2v4h4m-5-9A10 10 0 0 0 2 12a10 10 0 0 0 10 10a10 10 0 0 0 10-10A10 10 0 0 0 12 2"
      />
    </Group>
  );
}
